import { Voucher } from '@/lib/redux/features/voucher/type'
import { VoucherCodesValidateResult } from '@/lib/redux/features/voucher/voucherCodesValidate.types'
import { isVoucherApiErrorCode } from '@/constants/voucher/voucherErrorCodes'
import { extractVoucherApiError, getVoucherValidateErrorMessage } from './getVoucherValidateErrorMessage'
import { normalizeVoucher } from './normalizeVoucherCategories'

const toNum = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const voucherFromValidatePayload = (
  data: Record<string, unknown>,
  fallbackVoucher?: Voucher | null,
  discountAmount = 0,
): Voucher | null => {
  const code = data.voucherCode ?? data.code
  if (code == null || String(code).trim() === '') return null
  const amount = String(data.value ?? data.discountAmount ?? discountAmount)
  // Số dư voucher (vd CODE_SINGLE_USE_BALANCE như voucher trừ dần TEV001): validate trả ở root
  // response — giữ lại để modal chi tiết hiện Tổng / Còn lại / Đã dùng.
  const hasBalance =
    data.totalAmount != null || data.remainAmount != null || data.usedAmount != null
  return {
    ...(fallbackVoucher ?? {}),
    voucherId: String(data.voucherId ?? fallbackVoucher?.voucherId ?? code),
    ...(hasBalance
      ? {
          totalAmount: String(data.totalAmount ?? fallbackVoucher?.totalAmount ?? ''),
          remainAmount: String(data.remainAmount ?? fallbackVoucher?.remainAmount ?? ''),
          usingAmount: String(data.usedAmount ?? fallbackVoucher?.usingAmount ?? ''),
        }
      : {}),
    voucherCode: String(code),
    displayName: String(data.displayName ?? fallbackVoucher?.displayName ?? code),
    productType:
      (data.productType as Voucher['productType']) ??
      fallbackVoucher?.productType ??
      'FLIGHTQT',
    valueType:
      (data.valueType as Voucher['valueType']) ?? fallbackVoucher?.valueType ?? 'AMOUNT',
    value: amount,
    defaultAmount: String(data.defaultAmount ?? fallbackVoucher?.defaultAmount ?? amount),
    voucherCodeType: fallbackVoucher?.voucherCodeType ?? 'PROMO_PUBLIC',
    isEnable: fallbackVoucher?.isEnable ?? true,
    voucherCategoryCode: fallbackVoucher?.voucherCategoryCode ?? '',
    voucherQuantity: fallbackVoucher?.voucherQuantity ?? '0',
    createdDate: fallbackVoucher?.createdDate ?? '',
    startDate: fallbackVoucher?.startDate ?? '',
    endDate: fallbackVoucher?.endDate ?? '',
    initMethod: fallbackVoucher?.initMethod ?? '',
    status: fallbackVoucher?.status ?? '',
    showType: fallbackVoucher?.showType ?? 'media',
  } as Voucher
}

const invalidResult = (
  payload: unknown,
  partial?: Partial<VoucherCodesValidateResult>,
): VoucherCodesValidateResult => {
  const apiError = extractVoucherApiError(payload)
  return {
    valid: false,
    errorCode: apiError.errorCode ?? partial?.errorCode,
    reason:
      partial?.reason ??
      getVoucherValidateErrorMessage(
        apiError.errorCode != null ? apiError : payload,
      ),
    ...partial,
  }
}

export const normalizeVoucherCodesValidateResponse = (
  payload: unknown,
  fallbackVoucher?: Voucher | null,
): VoucherCodesValidateResult => {
  if (!payload || typeof payload !== 'object') {
    return invalidResult(payload)
  }

  const apiError = extractVoucherApiError(payload)
  if (apiError.errorCode) {
    return invalidResult(payload, { errorCode: apiError.errorCode })
  }

  const root = payload as Record<string, unknown>
  const data = (root.data && typeof root.data === 'object' ? root.data : root) as Record<
    string,
    unknown
  >

  const nestedError = extractVoucherApiError(data)
  if (nestedError.errorCode) {
    return invalidResult(payload, { errorCode: nestedError.errorCode })
  }

  const explicitInvalid =
    data.valid === false ||
    data.isValid === false ||
    data.success === false ||
    data.status === 'INVALID'

  if (explicitInvalid) {
    const code = toNum(data.errorCode ?? data.error_code)
    return invalidResult(payload, {
      errorCode: isVoucherApiErrorCode(code) ? code : undefined,
      reason: getVoucherValidateErrorMessage(payload),
    })
  }

  const discountAmount = toNum(
    data.discountAmount ?? data.appliedAmount ?? data.voucherValue ?? data.amount,
  )

  const explicitValid =
    data.valid === true || data.isValid === true || data.success === true || data.status === 'VALID'

  let voucher: Voucher | null | undefined = null
  const rawVoucher = data.voucher ?? data.voucherDetail ?? data.voucherInfo
  if (rawVoucher && typeof rawVoucher === 'object') {
    voucher = normalizeVoucher(rawVoucher as Parameters<typeof normalizeVoucher>[0])
  }
  if (!voucher) {
    voucher = voucherFromValidatePayload(data, fallbackVoucher, discountAmount)
  }

  const valid =
    explicitValid ||
    (discountAmount > 0 && (explicitValid || !!rawVoucher || !!data.voucherCode))

  if (!valid) {
    return invalidResult(payload)
  }

  const resolvedVoucher = voucher ?? fallbackVoucher ?? null

  return {
    valid: true,
    reason: data.reason != null ? String(data.reason) : data.message != null ? String(data.message) : undefined,
    discountAmount: discountAmount > 0 ? discountAmount : undefined,
    voucher: resolvedVoucher,
  }
}
